// @flow

import { toastr } from 'react-redux-toastr';
import Pincode from '../../../../models/pincode';
import {
  getFormattedTime,
  formattedStatus,
  nonRivigoUser,
  getRetailTableCellObject,
  rivigoUser,
  rivigoClient,
  checkForBlockedRole,
  retlClientId
} from '../../../../services/helpers';

export const existingPrqTableConfig = {
  columns: [
    {
      Header: 'PRQ ID',
      headerClassName: 'flex-25',
      accessor: 'id',
      className: 'flex-25'
    },
    {
      Header: 'Time',
      headerClassName: 'flex-25',
      className: 'flex-25',
      Cell: row => getFormattedTime(row.original.pickupSlotStartTime)
    },
    {
      Header: 'Weight | Pkgs',
      headerClassName: 'flex-25',
      className: 'flex-25 table-head-custom-retail',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.weightRange,
          row.original.numberOfBoxes
        )
    },
    {
      Header: 'Status',
      headerClassName: 'flex-25',
      className: 'flex-25',
      Cell: row => formattedStatus(row.original.pickupStatus)
    }
  ],
  className: 'existing-prq-table-wrapper',
  noDataText: 'No pickups found',
  minRows: 0,
  pageSizeOptions: [7, 10, 15, 25, 50, 100]
};

export const statusMap = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

export const daysOfWeekList = [
  {
    id: 0,
    text: 'MO',
    highLight: false
  },
  {
    id: 1,
    text: 'TU',
    highLight: false
  },
  {
    id: 2,
    text: 'WE',
    highLight: false
  },
  {
    id: 3,
    text: 'TH',
    highLight: false
  },
  {
    id: 4,
    text: 'FR',
    highLight: false
  },
  {
    id: 5,
    text: 'SA',
    highLight: false
  },
  {
    id: 6,
    text: 'SU',
    highLight: false
  }
];

export const cnoteTypeDict = {
  RETAIL: 'RETAIL',
  NORMAL_TO_PAY: 'NORMAL_TO_PAY',
  NORMAL: 'NORMAL'
};

export const getRequestDtoFromDropEntityArray = (
  dropEntityArray: Array,
  stateData: Object
) =>
  dropEntityArray
    .filter(
      dr => (dr.dropPincode && dr.dropPincode.code) || dr.pkgs || dr.weight
    )
    .map(dr => ({
      toPincode: dr.dropPincode && dr.dropPincode.code,
      numberOfBoxes: dr.pkgs,
      weight: dr.weight,
      edd: dr.dropPincode && dr.dropPincode.edd,
      approxPrice: dr.dropPincode && dr.dropPincode.approxPrice,
      id: dr.id,
      pickupId: dr.pickupId || stateData.pickupId,
      // the below are not just used to be passed to backend, these are populated from public create prq
      numberOfUnits: dr.numberOfUnits,
      length: dr.length,
      width: dr.width,
      height: dr.height,
      unit: dr.unit,
      fcDetailId: dr.fcDetailId,
      pickupProductCategoryId: dr.pickupProductCategoryId,
      pickupProductCategory: dr.pickupProductCategory
    }));

export const getDoWString = (dowlist: Array) => {
  const prearray = [];
  dowlist.forEach((dw, index) => {
    if (dw.highLight) {
      prearray[index] = 1;
    } else {
      prearray[index] = 0;
    }
  });
  return prearray.join(',');
};

export const getDaysOfWeekListFromString = (dowstring: string) => {
  const toreturn = daysOfWeekList;
  const strArray = dowstring && dowstring.split(',');
  if (strArray && strArray.length) {
    strArray.forEach((ds, index) => {
      if (ds === '1') {
        toreturn[index].highLight = true;
      } else {
        toreturn[index].highLight = false;
      }
    });
  }
  return toreturn;
};

export const getPostCallData = (stateData: Object, propsData: Object) => {
  const toreturn = {
    clientId: stateData.selectedClientObj && stateData.selectedClientObj.id,
    pinCode:
      stateData.selectedAddress &&
      stateData.selectedAddress.address &&
      stateData.selectedAddress.address.pincode,
    clientAddressId: stateData.selectedAddress && stateData.selectedAddress.id,
    contactPerson: stateData.contactPersonName,
    contactNumber: stateData.contactPersonNumber,
    pickupRequestDropDetailsDTOList: getRequestDtoFromDropEntityArray(
      stateData.dropEntityArray,
      stateData
    ),
    remarks: stateData.specialInstructions,
    isRecurring: Number(stateData.recurring) || 0,
    daysOfWeek: getDoWString(stateData.daysOfWeekList),
    suggestedCaptainId: propsData.suggestedCaptainId,
    selectedCaptainId:
      (stateData.selectedCaptain &&
        stateData.selectedCaptain.userDTO &&
        stateData.selectedCaptain.userDTO.id) ||
      (stateData.selectedCaptain && stateData.selectedCaptain.userId),
    suggestedBPId: propsData.suggestedBPId,
    pickupDate: stateData.pickupDate && stateData.pickupDate.valueOf(),
    pickupTimeSlot: stateData.pickupTimeSlot && stateData.pickupTimeSlot.key,
    id: stateData.pickupId,
    pickupStatus: stateData.pickupStatus, // forced
    lastUpdatedAt: stateData.fetchedData && stateData.fetchedData.lastUpdatedAt,
    selectedBPId: propsData.pickupData && propsData.pickupData.selectedBPId
  };
  return toreturn;
};

export const getTimeSlotsObjects = (timeSlotList: Array) => {
  if (!timeSlotList) {
    return [];
  }
  return timeSlotList.map(slot => ({
    val: slot,
    key: slot
  }));
};

export const prqComponentsDict = {
  cnoteSelectionTabs: {
    stateAccessor: 'selectedClientObj',
    renderFunction: 'renderClientSearchInput',
    hideFn: userProfile => {
      if (nonRivigoUser(userProfile)) {
        return true;
      }
      if (
        userProfile.authorities &&
        userProfile.authorities.indexOf('ROLE_CLIENT_CREATE_PICKUP') !== -1
      ) {
        return true;
      }
      return false;
    },
    disableFn: () => false
  },
  client: {
    stateAccessor: 'selectedClientObj',
    type: {},
    required: true,
    requiredFailedMessage: 'Please select a client',
    validationFunction: null,
    validationFailedMessage: 'Please select a valid client',
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderClientSearchInput'
  },
  selectedAddress: {
    stateAccessor: 'selectedAddress',
    type: {},
    requiredNestedKey: 'id',
    required: true,
    requiredFailedMessage: 'Please select a client address',
    validationFunction: null,
    validationFailedMessage: 'Please select a client address',
    errorMessageStyleClass: 'error-info'
  },
  pickupDate: {
    stateAccessor: 'pickupDate',
    type: {},
    required: true,
    requiredFailedMessage: 'Please select pickup date and slot',
    validationFunction: null,
    validationFailedMessage: 'Please select a valid pickup date and slot',
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderDateSlotDropdowns'
  },
  pickupTimeSlot: {
    stateAccessor: 'pickupTimeSlot',
    type: {},
    requiredNestedKey: 'key',
    required: true,
    requiredFailedMessage: 'Please select pickup date and slot',
    validationFunction: null,
    validationFailedMessage: 'Please select a valid pickup date and slot',
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderDateSlotDropdowns'
  },
  contactPersonName: {
    stateAccessor: 'contactPersonName',
    type: '',
    required: true,
    requiredFailedMessage: 'Please enter contact person',
    validationFunction: null,
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderContactPersonInputs'
  },
  contactPersonNumber: {
    stateAccessor: 'contactPersonNumber',
    type: '',
    required: true,
    requiredFailedMessage: "Please enter contact person's number",
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderContactPersonInputs'
  },
  dropEntityArray: {
    stateAccessor: 'dropEntityArray',
    type: [],
    required: true,
    requiredFailedMessage: 'Please enter drop pincode',
    validationFunction: (dropEntityArray, stateInstance) => {
      let msgToReturn = '';
      let allWeightsPresent = true;
      let allPincodesPresent = true;
      if (dropEntityArray) {
        dropEntityArray.forEach(de => {
          if (!de.weight || parseFloat(de.weight) === 0) {
            allWeightsPresent = false;
          }
          if (!de.dropPincode.code) {
            allPincodesPresent = false;
          }
        });
        if (!allWeightsPresent && !allPincodesPresent) {
          return 'Please enter drop pincode and weight';
        } else if (!allWeightsPresent) {
          return 'Please enter weight';
        } else if (!allPincodesPresent) {
          return 'Please enter drop pincode';
        } else {
          return '';
        }
      }
      if (
        stateInstance.selectedClientObj &&
        stateInstance.selectedClientObj.cnoteType === 'NORMAL'
      ) {
        return true;
      }
      if (
        stateInstance.fetchedData &&
        stateInstance.prqV2ReleaseTime &&
        stateInstance.prqV2ReleaseTime > stateInstance.fetchedData.createdAt
      ) {
        return true;
      }
      const { disableDropPincodeClientListString } = stateInstance;
      const arrayFromString = (disableDropPincodeClientListString &&
        disableDropPincodeClientListString.split(',')) || ['32'];
      if (
        stateInstance.selectedClientObj &&
        arrayFromString.indexOf(stateInstance.selectedClientObj.id.toString()) >
          -1
      ) {
        // Special case - need to skip drop pincode validation for JCKY and JCKY like clients.
        return true;
      }
      dropEntityArray.forEach(de => {
        if (de.dropPincode) {
          if (!de.dropPincode.code) {
            msgToReturn = 'Please enter drop pincode';
          } else if (!de.dropPincode.serviceable) {
            msgToReturn = 'Drop pincode not serviceable';
          }
        }
      });
      return msgToReturn;
    },
    validationFailedMessage: 'Please enter valid drop values',
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderContactPersonInputs'
  },
  recurring: {
    stateAccessor: 'recurring',
    type: [],
    required: false,
    requiredFailedMessage: null,
    validationFunction: (recurring, stateInstance) => {
      let missedCount = 0;
      if (recurring) {
        stateInstance.daysOfWeekList.forEach(day => {
          if (!day.highLight) {
            missedCount += 1;
          }
        });
      }
      return missedCount !== 7;
    },
    validationFailedMessage: 'Please choose one recurring day',
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderContactPersonInputs'
  },
  assignTo: {
    stateAccessor: 'selectedCaptain',
    type: {},
    required: false,
    requiredFailedMessage: null,
    hideFn: (userProfile, propsData) => {
      if (userProfile.authorities) {
        if (
          userProfile.authorities.indexOf('ROLE_VIEW_PICKUP_ASSIGNMENT') === -1
        ) {
          return true;
        }
      }
      if (propsData.pickupData) {
        if (
          propsData.pickupData.pickupStatus === 'DELETED' ||
          propsData.pickupData.pickupStatus === 'COMPLETE' ||
          propsData.pickupData.pickupStatus === 'REACHED_AT_CLIENT_WAREHOUSE'
        ) {
          return true;
        }
      }
      if (nonRivigoUser(userProfile)) {
        return true;
      }
      if (!propsData.captainList) {
        return true;
      }
      if (propsData.captainList && !propsData.captainList.length) {
        return true;
      }
      return false;
    },
    validationFailedMessage: null,
    errorMessageStyleClass: 'error-info',
    renderFunction: 'renderContactPersonInputs'
  }
};

export const findCaptainByUserId = (captainId, captainList) => {
  let toreturn;
  if (captainList && captainList.length) {
    captainList.forEach(cp => {
      if (cp && cp.userDTO && cp.userDTO.id === captainId) {
        toreturn = cp;
      } else if (cp.userId === captainId) {
        toreturn = cp;
      }
    });
  }
  return toreturn;
};

export const getDropEntityFromRequestDto = (dropPincodeArray: Array) => {
  if (dropPincodeArray && dropPincodeArray.length) {
    return dropPincodeArray.map(dp => ({
      dropPincode: new Pincode(dp.toPincode, true, true),
      weight: dp.weight && dp.weight.toString(), // as input type is text
      pkgs: dp.numberOfBoxes && dp.numberOfBoxes.toString(), // as input type is text
      edd: dp.edd,
      id: dp.id,
      pickupId: dp.pickupId,
      numberOfUnits: dp.numberOfUnits,
      length: dp.length,
      width: dp.width,
      height: dp.height,
      unit: dp.unit,
      fcDetailId: dp.fcDetailId,
      pickupProductCategoryId: dp.pickupProductCategoryId,
      pickupProductCategory: dp.pickupProductCategory
    }));
  }
  return [
    {
      dropPincode: new Pincode(''),
      weight: '',
      pkgs: ''
    }
  ];
};

export const pickupAssignToDisplayAccessor = (elem: Object) => {
  let toreturn = elem.name;
  toreturn += elem.role ? ` (${elem.role})` : '';
  toreturn += elem.code ? ` (${elem.code})` : '';
  if (elem.feederVendorId) {
    toreturn += ' (AIR)';
  }
  return toreturn;
};

export const voidSelectedAddress = (address: Object) =>
  !address || (address && !address.id);

export const getCnoteTypeFromIndex = selectedCnoteTypeIndex =>
  selectedCnoteTypeIndex === 0 ? 'NORMAL' : 'RETAIL';

export const getCnoteIndexFromType = cnoteType =>
  cnoteType === 'NORMAL' ? 0 : 1;

export const getBlockedClientsForPickup = (
  filteredBlockClientsPickupNormal: Object,
  blockedClients: Array<any>
) => {
  if (
    !filteredBlockClientsPickupNormal ||
    !filteredBlockClientsPickupNormal.length
  ) {
    return [];
  }
  return filteredBlockClientsPickupNormal.filter(
    cl => blockedClients && !(blockedClients.indexOf(cl.clientCode) >= 0)
  );
};

export const getOptionsArrayObject = (
  clientList,
  cnoteType,
  userProfile,
  isEditPrq
) => {
  if (rivigoUser(userProfile) && !isEditPrq) {
    clientList = clientList.filter(rivigoClient);
  }

  if (cnoteType === 'NORMAL') {
    clientList.forEach(cl => {
      cl.displayName = `${cl.displayName} (${cl.clientCode})`;
    });
    // filter the client list from blocked clients list only if user has role blocked clients and it is Create Prq
    if (checkForBlockedRole(userProfile) && !isEditPrq) {
      return getBlockedClientsForPickup(
        clientList,
        userProfile &&
          userProfile.sessionProperties &&
          userProfile.sessionProperties.BLOCKED_CLIENTS
      );
    }
  }
  return clientList;
};

export const determineOrganizationIdForAddressFetch = (
  selectedClientObj,
  userProfile
) => {
  if (selectedClientObj && selectedClientObj.id === retlClientId) {
    return userProfile && userProfile.organizationId;
  }
  return selectedClientObj && selectedClientObj.organizationId;
};

export const getVisibleAddressFields = () => {
  return ['name', 'detailedAddress', 'state', 'pincode', 'taxId'];
};

export const showErrorToastr = message => {
  toastr.error('Error', message);
};

export const clientDetailsFormConfig = {
  client: {
    stateAccessor: 'selectedClientObj',
    requiredFailedMessage: 'Please select a client',
    required: true,
    validationFailed: false,
    validationFailedMessage: 'Please select a client'
  },
  selectedAddress: {
    stateAccessor: 'selectedAddress',
    requiredFailedMessage: 'Please select a client address',
    required: true,
    validationFailed: false,
    validationFailedMessage: 'Please select a client address'
  },
  pickupDate: {
    stateAccessor: 'pickupDate',
    requiredFailedMessage: 'Please select pickup date',
    required: true,
    validationFailed: false,
    validationFailedMessage: 'Please select pickup date'
  },
  pickupTimeSlot: {
    stateAccessor: 'pickupTimeSlot',
    requiredFailedMessage: 'Please select pickup slot',
    required: true,
    validationFailed: false,
    validationFailedMessage: 'Please select pickup slot'
  },
  contactPersonDetails: {
    stateAccessor: 'contactDetails',
    requiredFailedMessage: 'Please enter complete contact details',
    required: true,
    validationFailed: false,
    validationFailedMessage: 'Please enter complete contact details'
  }
};

export const packageDetailsFormConfig = {
  lineOne: {
    weight: {
      headerDisplay: 'Wt(in kgs)',
      stateAccessor: 'weight',
      requiredFailedMessage: 'Please enter weight',
      allowedInputKeys: 'decimalnumber',
      required: true,
      validationFailed: false
    },
    length: {
      headerDisplay: 'Length',
      stateAccessor: 'length',
      allowedInputKeys: 'decimalnumber',
      required: false,
      validationFailed: false
    },
    width: {
      headerDisplay: 'Width',
      stateAccessor: 'width',
      allowedInputKeys: 'decimalnumber',
      required: false,
      validationFailed: false
    },
    height: {
      headerDisplay: 'Height',
      stateAccessor: 'height',
      allowedInputKeys: 'decimalnumber',
      required: false,
      validationFailed: false
    },
    numberOfBoxes: {
      headerDisplay: 'No. of Packages',
      stateAccessor: 'numberOfBoxes',
      allowedInputKeys: 'number',
      required: false,
      validationFailed: false
    }
  },
  lineTwo: [
    {
      stateAccessor: 'numberOfUnits',
      allowedInputKeys: 'onlynumber',
      required: false,
      placeholder: 'No. of Units',
      validationFailed: false
    }
  ]
};

export const shipmentDetailsConfig = {
  clientName: 'clientName',
  packageArray: 'packageDetails',
  fcDetailsArray: 'fcDetailsArray'
};

export const modernTradeClients = {
  AMAZON: 'AMAZON',
  OTHERS: 'OTHERS'
};

export const fcDetailsConfig = {
  selectTradeClient: 'selectTradeClient',
  selectedTimeSlot: 'selectedTimeSlot',
  selectedFcCode: 'selectedFcCode',
  appointmentId: 'appointmentId',
  shipmentId: 'shipmentId',
  selectedDate: 'selectedDate',
  packageDetails: 'packageDetails',
  pinCode: 'pinCode',
  isPincodeServiceable: 'isPincodeServiceable',
  timeSlotList: 'timeSlotList'
};

export const getFormattedList = (listOfStrings: Array<any>) => {
  if (!listOfStrings) {
    return [];
  }
  return listOfStrings.map(fcCode => {
    return { key: fcCode, val: fcCode };
  });
};

export const leftTabPanels = {
  clientDetailsObj: {
    stateAccessor: 'clientDetailsObj',
    displayName: 'Client Details',
    index: 0
  },
  shipmentDetailsObj: {
    stateAccessor: 'shipmentDetailsObj',
    displayName: 'Shipment Details',
    index: 1
  },
  summary: {
    stateAccessor: 'summary',
    displayName: 'Summary',
    index: 2
  }
};

export const getRequestDtoFromPackageDetailsArray = (
  packageDetailsArray: Array,
  dropPincode: string
) =>
  (packageDetailsArray || []).map(dr => ({
    toPincode: dropPincode,
    weight: dr.weight,
    pickupProductCategoryId:
      typeof dr.selectedPackage === 'object' ? dr.selectedPackage.id : null,
    pickupProductCategory:
      typeof dr.selectedPackage === 'object'
        ? dr.selectedPackage.category
        : dr.selectedPackage,
    numberOfBoxes: dr.numberOfBoxes,
    numberOfUnits: dr.numberOfUnits,
    length: dr.length,
    width: dr.width,
    height: dr.height,
    unit: dr.unit && dr.unit.key
  }));

export const getRequestDtoFromFcDetailsArray = (
  fcDetailsArray: Array,
  stateData: Object
) => {
  return (fcDetailsArray || []).map((fc, index) => ({
    shipmentNo: index + 1,
    modernTradeClient:
      (stateData.shipmentDetailsObj &&
        stateData.shipmentDetailsObj[shipmentDetailsConfig.clientName]) ||
      (stateData.shipmentDetailsObj &&
        stateData.shipmentDetailsObj.selectTradeClient &&
        stateData.shipmentDetailsObj.selectTradeClient.displayName),
    modernTradeClientId:
      stateData.shipmentDetailsObj &&
      stateData.shipmentDetailsObj.selectTradeClient &&
      stateData.shipmentDetailsObj.selectTradeClient.id,
    fcCode: fc.selectedFcCode && fc.selectedFcCode.key,
    pinCode: fc[fcDetailsConfig.pinCode],
    poShipmentId: fc[fcDetailsConfig.shipmentId],
    appointmentId: fc[fcDetailsConfig.appointmentId],
    appointmentDate:
      fc[fcDetailsConfig.selectedDate] &&
      fc[fcDetailsConfig.selectedDate].valueOf(),
    appointmentTime:
      fc[fcDetailsConfig.selectedTimeSlot] &&
      fc[fcDetailsConfig.selectedTimeSlot].key,
    packageDetails: getRequestDtoFromPackageDetailsArray(
      fc[fcDetailsConfig.packageDetails],
      fc[fcDetailsConfig.pinCode]
    )
  }));
};

export const getPickUpDtoForPublicCreatePrq = stateData => {
  const toReturn = {};

  if (stateData.clientDetailsObj) {
    if (stateData.clientDetailsObj.selectedClientObj) {
      toReturn.clientId = stateData.clientDetailsObj.selectedClientObj.id;
    }

    if (
      stateData.clientDetailsObj.selectedAddress &&
      stateData.clientDetailsObj.selectedAddress.address
    ) {
      toReturn.clientAddressId = stateData.clientDetailsObj.selectedAddress.id;
      toReturn.pinCode =
        stateData.clientDetailsObj.selectedAddress.address.pincode;
      toReturn.contactPerson =
        stateData.clientDetailsObj.contactDetails.contactPerson;
      toReturn.contactNumber =
        stateData.clientDetailsObj.contactDetails.phoneNumber;
    }

    toReturn.pickupDate =
      stateData.clientDetailsObj.pickupDate &&
      stateData.clientDetailsObj.pickupDate.valueOf();
    toReturn.pickupTimeSlot =
      stateData.clientDetailsObj.pickupTimeSlot &&
      stateData.clientDetailsObj.pickupTimeSlot.key;

    if (
      stateData.shipmentDetailsObj &&
      stateData.shipmentDetailsObj.fcDetailsArray &&
      stateData.shipmentDetailsObj.fcDetailsArray.length
    ) {
      toReturn.pickupFcDetailDTOs = getRequestDtoFromFcDetailsArray(
        stateData.shipmentDetailsObj.fcDetailsArray,
        stateData
      );
    }
  }

  return toReturn;
};

export const defaultShipmentDetailsObject = () => ({
  [shipmentDetailsConfig.packageArray]: [{}],
  [shipmentDetailsConfig.fcDetailsArray]: [
    {
      [fcDetailsConfig.packageDetails]: [{}],
      [fcDetailsConfig.isPincodeServiceable]: true
    }
  ]
});
